"use client";

import { motion, useAnimation, useReducedMotion, Variants } from "framer-motion";
import { forwardRef, useCallback, useImperativeHandle, useRef } from "react";

export interface TrendingUpDownIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface TrendingUpDownIconProps {
  size?: number;
  duration?: number;
  isAnimated?: boolean;
  isHovered?: boolean;
  className?: string;
}

const TrendingUpDownIcon = forwardRef<TrendingUpDownIconHandle, TrendingUpDownIconProps>(
  (
    {
      className,
      size = 24,
      duration = 1,
      isAnimated = true,
      isHovered = false,
    },
    ref,
  ) => {
    const controls = useAnimation();
    const reduced = useReducedMotion();
    const isControlled = useRef(false);
    const prevHovered = useRef(isHovered);

    useImperativeHandle(ref, () => {
      isControlled.current = true;
      return {
        startAnimation: () =>
          reduced ? controls.start("normal") : controls.start("animate"),
        stopAnimation: () => controls.start("normal"),
      };
    });

    // React to isHovered prop changes
    if (isHovered !== prevHovered.current) {
      prevHovered.current = isHovered;
      if (isHovered && isAnimated && !reduced) {
        controls.start("animate");
      } else if (!isHovered) {
        controls.start("normal");
      }
    }

    const handleEnter = useCallback(() => {
      if (!isAnimated || reduced) return;
      if (!isControlled.current) controls.start("animate");
    }, [controls, reduced, isAnimated]);

    const handleLeave = useCallback(() => {
      if (!isControlled.current) controls.start("normal");
    }, [controls]);

    const iconVariants: Variants = {
      normal: { scale: 1, rotate: 0 },
      animate: {
        scale: [1, 1.05, 0.98, 1],
        rotate: [0, 2, -2, 0],
        transition: {
          duration: 0.9 * duration,
          ease: [0.22, 1, 0.36, 1],
        },
      },
    };

    const upPathVariants: Variants = {
      normal: { pathLength: 1, pathOffset: 0 },
      animate: {
        pathLength: [0, 1],
        pathOffset: [1, 0],
        transition: {
          duration: 0.7 * duration,
          ease: "easeInOut",
        },
      },
    };

    const downPathVariants: Variants = {
      normal: { opacity: 1, x: 0, y: 0 },
      animate: {
        opacity: [0, 1],
        x: [4, 0],
        y: [-4, 0],
        transition: {
          duration: 0.55 * duration,
          ease: [0.16, 1, 0.3, 1],
          delay: 0.15,
        },
      },
    };

    return (
      <motion.svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        initial="normal"
        animate={controls}
        variants={iconVariants}
        className={className}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        style={{ flexShrink: 0 }}
      >
        <motion.path
          d="M14.828 14.828 21 21"
          variants={downPathVariants}
          initial="normal"
          animate={controls}
        />
        <motion.path
          d="M21 16v5h-5"
          variants={downPathVariants}
          initial="normal"
          animate={controls}
        />
        <motion.path
          d="m21 3-9 9-4-4-6 6"
          variants={upPathVariants}
          initial="normal"
          animate={controls}
        />
        <motion.path
          d="M21 8V3h-5"
          variants={upPathVariants}
          initial="normal"
          animate={controls}
        />
      </motion.svg>
    );
  },
);

TrendingUpDownIcon.displayName = "TrendingUpDownIcon";
export { TrendingUpDownIcon };
